import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { BILL_ALLOCATION_STATE } from 'src/api/billAutomation/bills';
import { getSuggestionKey, hasOverlappingBills, isAwaitingSpikeReview, mapAllocatedTable, mapSuggestionTable } from 'src/components/allocations/AllocationSuggestion';
import { formatServicePeriod, getServiceDates, getServiceTotal } from './billDetailsUtils';
import { getAllocationSetting, toNumber } from './billEditUtils';

/**
 * One allocation table per property and utility the bill is allocated for, in the order the
 * extraction lists them. Only the inputs are worked out here — the service dates, the amount, the
 * deduction and which property utility they belong to; the charges themselves come from the
 * allocation service. `goodwillDeductions` are what the properties have configured, keyed by
 * property and utility.
 */
export function buildAllocationTables(_ref) {
  let data = _ref.data,
    goodwillDeductions = _ref.goodwillDeductions,
    groups = _ref.groups,
    settings = _ref.settings;
  return groups.map(group => ({
    address: group.address,
    propertyIndex: group.propertyIndex,
    utilities: group.services.map(_ref2 => {
      var _setting$serviceStart, _setting$serviceEndDa, _goodwillDeductions$g, _toNumber;
      let key = _ref2.key,
        service = _ref2.service,
        target = _ref2.target;
      const propertyIndex = group.propertyIndex;
      const setting = getAllocationSetting(settings, propertyIndex, service.serviceType);
      const extracted = getServiceDates(service, data);
      const extractedAmount = getServiceTotal(service);
      const serviceStartDate = (_setting$serviceStart = setting.serviceStartDate) !== null && _setting$serviceStart !== void 0 ? _setting$serviceStart : extracted.start;
      const serviceEndDate = (_setting$serviceEndDa = setting.serviceEndDate) !== null && _setting$serviceEndDa !== void 0 ? _setting$serviceEndDa : extracted.end;
      // An emptied amount field falls back to the extraction rather than allocating nothing.
      const overriddenAmount = toNumber(setting.billAmount);
      const billAmount = overriddenAmount === null ? extractedAmount : overriddenAmount;
      const configuredGwd = (_goodwillDeductions$g = goodwillDeductions === null || goodwillDeductions === void 0 ? void 0 : goodwillDeductions[getSuggestionKey(target.propertyId, target.utilityId)]) !== null && _goodwillDeductions$g !== void 0 ? _goodwillDeductions$g : null;
      return _objectSpread(_objectSpread({}, target), {}, {
        billAmount,
        extractedAmount,
        extractedPeriod: formatServicePeriod(extracted.start, extracted.end),
        // Untouched, what the property utility is configured to deduct is what is allocated.
        gwdOverride: (_toNumber = toNumber(setting.gwdPercent)) !== null && _toNumber !== void 0 ? _toNumber : configuredGwd,
        // Shown until the service answers with the deduction it actually applied.
        gwdPercent: configuredGwd !== null && configuredGwd !== void 0 ? configuredGwd : 0,
        key,
        propertyIndex,
        serviceEndDate,
        servicePeriod: formatServicePeriod(serviceStartDate, serviceEndDate),
        serviceStartDate,
        serviceType: service.serviceType,
        setting
      });
    })
  }));
}

/** The allocation states by property and utility, which is how the tables are keyed. */
export function indexAllocationStates(services) {
  return (services || []).reduce((acc, service) => {
    const key = getSuggestionKey(service === null || service === void 0 ? void 0 : service.propertyId, service === null || service === void 0 ? void 0 : service.utilityId);
    if (key) acc[key] = service;
    return acc;
  }, {});
}

/**
 * Whether there is a saved allocation table to read for this service. Only from the point the
 * allocation service has worked one out — before that the suggestion is all there is to show.
 */
export function hasAllocationTable(service) {
  return !!(service !== null && service !== void 0 && service.billingCycleId) && [BILL_ALLOCATION_STATE.allocated, BILL_ALLOCATION_STATE.awaitingApproval, BILL_ALLOCATION_STATE.needsReview, BILL_ALLOCATION_STATE.skipped].includes(service.state);
}

/**
 * Whether the allocation service is holding this service back until the bill has been compared with
 * the cycles before it, which is when its comparison is worth reading.
 */
export function isHeldForReview(service) {
  return !!(service !== null && service !== void 0 && service.billingCycleId) && (service === null || service === void 0 ? void 0 : service.state) === BILL_ALLOCATION_STATE.needsReview;
}

/**
 * Folds into the tables what the allocation service says about them: the saved allocation table for
 * a service that has been allocated, and the suggestion for one that has not. Both arrive in the
 * same shape, so a table reads the same either way — what changes is that the allocated one is what
 * residents will actually be charged. Tables still waiting on an answer keep their inputs and show
 * unknown totals rather than zeros.
 */
export function withAllocations(groups, _ref3) {
  let allocatedTables = _ref3.allocatedTables,
    allocationStates = _ref3.allocationStates,
    comparisons = _ref3.comparisons,
    getResidentName = _ref3.getResidentName,
    hasFailed = _ref3.hasFailed,
    isLoading = _ref3.isLoading,
    reviewingKeys = _ref3.reviewingKeys,
    suggestions = _ref3.suggestions;
  return groups.map(group => _objectSpread(_objectSpread({}, group), {}, {
    utilities: group.utilities.map(table => {
      var _allocationStates$key, _allocation$state, _allocation$error, _mapped$allocationMet, _mapped$cadPercent, _comparisons$key, _mapped$deductionPerc, _ref4, _mapped$gwdPercent, _mapped$overlappingBi, _mapped$recoveryPerce, _mapped$skipReason, _mapped$totalBilling, _mapped$units;
      const key = getSuggestionKey(table.propertyId, table.utilityId);
      const allocation = (_allocationStates$key = allocationStates === null || allocationStates === void 0 ? void 0 : allocationStates[key]) !== null && _allocationStates$key !== void 0 ? _allocationStates$key : null;
      const state = (_allocation$state = allocation === null || allocation === void 0 ? void 0 : allocation.state) !== null && _allocation$state !== void 0 ? _allocation$state : BILL_ALLOCATION_STATE.notAllocated;

      // The saved table wins: once a service is allocated, suggesting something else would be
      // showing figures nobody will be charged.
      const allocated = mapAllocatedTable(allocatedTables === null || allocatedTables === void 0 ? void 0 : allocatedTables[key], getResidentName);
      const mapped = allocated !== null && allocated !== void 0 ? allocated : mapSuggestionTable(suggestions === null || suggestions === void 0 ? void 0 : suggestions[key], getResidentName);
      // The bill an allocated service overlaps is the one it was allocated as. Said of a service
      // still to be allocated it is a warning; said of this one it is the service's own shadow.
      const isAllocatedState = state === BILL_ALLOCATION_STATE.allocated;
      return _objectSpread(_objectSpread({}, table), {}, {
        allocationError: (_allocation$error = allocation === null || allocation === void 0 ? void 0 : allocation.error) !== null && _allocation$error !== void 0 ? _allocation$error : null,
        allocationMethod: (_mapped$allocationMet = mapped === null || mapped === void 0 ? void 0 : mapped.allocationMethod) !== null && _mapped$allocationMet !== void 0 ? _mapped$allocationMet : null,
        allocationState: state,
        cadPercent: (_mapped$cadPercent = mapped === null || mapped === void 0 ? void 0 : mapped.cadPercent) !== null && _mapped$cadPercent !== void 0 ? _mapped$cadPercent : 0,
        comparison: (_comparisons$key = comparisons === null || comparisons === void 0 ? void 0 : comparisons[key]) !== null && _comparisons$key !== void 0 ? _comparisons$key : null,
        deductionPercent: (_mapped$deductionPerc = mapped === null || mapped === void 0 ? void 0 : mapped.deductionPercent) !== null && _mapped$deductionPerc !== void 0 ? _mapped$deductionPerc : null,
        gwdPercent: (_ref4 = (_mapped$gwdPercent = mapped === null || mapped === void 0 ? void 0 : mapped.gwdPercent) !== null && _mapped$gwdPercent !== void 0 ? _mapped$gwdPercent : table.gwdPercent) !== null && _ref4 !== void 0 ? _ref4 : 0,
        hasFailed: !!hasFailed && !mapped,
        isAllocated: !!allocated,
        isApproved: isAllocatedState,
        isComputable: mapped ? mapped.isComputable : true,
        // An allocated table is worked out and saved; nothing is being waited on for it.
        isLoading: !allocated && !!isLoading,
        isReviewing: (reviewingKeys || []).includes(key),
        overlappingBills: isAllocatedState ? [] : (_mapped$overlappingBi = mapped === null || mapped === void 0 ? void 0 : mapped.overlappingBills) !== null && _mapped$overlappingBi !== void 0 ? _mapped$overlappingBi : [],
        recoveryPercent: (_mapped$recoveryPerce = mapped === null || mapped === void 0 ? void 0 : mapped.recoveryPercent) !== null && _mapped$recoveryPerce !== void 0 ? _mapped$recoveryPerce : null,
        skipReason: (_mapped$skipReason = mapped === null || mapped === void 0 ? void 0 : mapped.skipReason) !== null && _mapped$skipReason !== void 0 ? _mapped$skipReason : null,
        totalBilling: (_mapped$totalBilling = mapped === null || mapped === void 0 ? void 0 : mapped.totalBilling) !== null && _mapped$totalBilling !== void 0 ? _mapped$totalBilling : null,
        units: (_mapped$units = mapped === null || mapped === void 0 ? void 0 : mapped.units) !== null && _mapped$units !== void 0 ? _mapped$units : []
      });
    })
  }));
}

/**
 * A table can only be approved once it holds charges to approve, and not if the property has already
 * been billed for these days on this utility — the same reason the bill would be refused on save.
 */
export function isApprovable(table) {
  return !table.needsProperty && !table.needsUtility && table.isComputable && !hasOverlappingBills(table) && (table.units || []).length > 0;
}

/**
 * Whether pressing approve on this table would do anything. A service already on its way is not
 * this card's to move; one held back for review is, once the comparison has been reviewed.
 */
export function isPendingAllocation(table) {
  var _table$allocationStat;
  const state = (_table$allocationStat = table.allocationState) !== null && _table$allocationStat !== void 0 ? _table$allocationStat : BILL_ALLOCATION_STATE.notAllocated;
  if (state === BILL_ALLOCATION_STATE.needsReview) return isApprovable(table) && !isAwaitingSpikeReview(table);
  return isApprovable(table) && [BILL_ALLOCATION_STATE.notAllocated, BILL_ALLOCATION_STATE.failed].includes(state);
}